import { useCallback, useEffect, useMemo, useState } from "react";
import api, { BACKEND } from "./api";

/**
 * Central hook for consuming CMS-managed trainers/members.
 * Same data source everywhere on the public site.
 *
 * Backend `/api/cms/members` already:
 *  - filters out inactive members (active !== false)
 *  - sorts featured first, then by `order`, then by name
 */
export function useTrainers({ limit, department, specialization, featuredOnly } = {}) {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/cms/members");
      setAll(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (e) {
      setAll([]);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const trainers = useMemo(() => {
    let list = all;
    if (featuredOnly) list = list.filter((t) => t.featured);
    if (department && department !== "All")
      list = list.filter((t) => (t.department || "").toLowerCase() === department.toLowerCase());
    if (specialization && specialization !== "All")
      list = list.filter((t) => (t.specialization || "").toLowerCase() === specialization.toLowerCase());
    if (limit) list = list.slice(0, limit);
    return list;
  }, [all, department, specialization, featuredOnly, limit]);

  const departments = useMemo(
    () => Array.from(new Set(all.map((t) => t.department).filter(Boolean))),
    [all]
  );

  return { trainers, all, loading, error, reload: load, departments };
}

export function resolveTrainerImage(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${BACKEND}${url}`;
}

export function trainerImageStyle(t) {
  // imagePositionX / imagePositionY: -100 → 0% edge, 0 → 50% center, 100 → 100% edge
  const x    = t?.imagePositionX ?? 0;
  const y    = t?.imagePositionY ?? 0;
  const zoom = t?.imageZoom ?? 100;          // 100-200: zoom into the image
  const fit  = t?.imageObjectFit || "cover"; // "cover" | "contain" | "fill"

  const posX = `${50 + x / 2}%`;
  const posY = `${50 + y / 2}%`;

  return {
    objectFit: fit,
    objectPosition: `${posX} ${posY}`,
    ...(zoom !== 100 ? { transform: `scale(${zoom / 100})`, transformOrigin: `${posX} ${posY}` } : {}),
  };
}

export function trainerInitials(name) {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");
}
